import type { messages } from "../i18n";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

export function buildModalDialog(
  id: string,
  titleText: string,
  bodyText: string,
  closeButtonText = "Close"
): HTMLDialogElement {
  const dialog = el("dialog", "info-dialog legal-dialog");
  dialog.id = id;
  dialog.setAttribute("aria-labelledby", `${id}-title`);

  const panel = el("div", "dialog-panel legal-dialog-panel");

  // Header with icon, title, and close X button
  const header = el("div", "legal-dialog-header");
  const headerLeft = el("div", "legal-dialog-header-left");

  const iconWrap = el("div", "legal-dialog-icon");
  if (id.includes("privacy")) {
    iconWrap.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    `;
  } else {
    iconWrap.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    `;
  }

  const titleNode = el("h3", "legal-dialog-title", titleText);
  titleNode.id = `${id}-title`;
  headerLeft.append(iconWrap, titleNode);

  const closeIconBtn = el("button", "legal-dialog-close-btn");
  closeIconBtn.type = "button";
  closeIconBtn.setAttribute("aria-label", closeButtonText);
  closeIconBtn.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  `;
  closeIconBtn.addEventListener("click", () => dialog.close());

  header.append(headerLeft, closeIconBtn);
  panel.append(header);

  // Body container
  const bodyWrap = el("div", "legal-dialog-body");
  const paragraphs = bodyText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  paragraphs.forEach((para, idx) => {
    const match = para.match(/^(\d+)[\.、]\s*([^：:]+)[：:]([\s\S]*)$/);
    if (match) {
      const itemNum = match[1];
      const itemTitle = match[2].trim();
      const itemDesc = match[3].trim();

      const itemCard = el("div", "legal-item-card");

      const itemTop = el("div", "legal-item-header");
      const numBadge = el("span", "legal-item-num", itemNum);
      const itemHeading = el("h4", "legal-item-title", itemTitle);
      itemTop.append(numBadge, itemHeading);

      const descP = el("p", "legal-item-desc", itemDesc);

      itemCard.append(itemTop, descP);
      bodyWrap.append(itemCard);
    } else {
      const introP = el("p", idx === 0 ? "legal-dialog-intro" : "legal-dialog-p", para);
      bodyWrap.append(introP);
    }
  });

  panel.append(bodyWrap);

  // Footer with localized confirm button
  const footer = el("div", "legal-dialog-footer");
  const closeButton = el("button", "secondary-button legal-dialog-confirm-btn", closeButtonText);
  closeButton.type = "button";
  closeButton.addEventListener("click", () => dialog.close());
  footer.append(closeButton);
  panel.append(footer);

  dialog.append(panel);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  return dialog;
}

export function buildSiteFooter(copy: typeof messages["zh-TW"]) {
  const footer = el("footer", "site-footer");
  const footerInner = el("div", "footer-inner");
  const legal = el("div", "footer-links");

  const privacyButton = el("button", "text-link-button", copy.footer.privacy);
  privacyButton.type = "button";
  privacyButton.addEventListener("click", () => {
    document.querySelector<HTMLDialogElement>("#privacy-dialog")?.showModal();
  });

  const termsButton = el("button", "text-link-button", copy.footer.terms);
  termsButton.type = "button";
  termsButton.addEventListener("click", () => {
    document.querySelector<HTMLDialogElement>("#terms-dialog")?.showModal();
  });

  legal.append(privacyButton, termsButton);

  const currentYear = new Date().getFullYear();
  const copyrightText = copy.footer.copyright.replace("{year}", String(currentYear));

  footerInner.append(
    el("p", "footer-brand", `${copy.brand.name} · ${copy.brand.tool}`),
    legal,
    el("p", "footer-copy", copyrightText),
    el("p", "footer-disclaimer", `${copy.disclaimer.title}：${copy.evidence.disclaimer}`),
    el("p", "footer-privacy-statement", copy.evidence.privacy)
  );
  footer.append(footerInner);

  return {
    footer,
    modals: [
      buildModalDialog("privacy-dialog", copy.privacyModal.title, copy.evidence.privacyBody, copy.common.close),
      buildModalDialog("terms-dialog", copy.termsModal.title, copy.termsModal.body, copy.common.close)
    ]
  };
}
